/* eslint-disable react/no-unused-prop-types */
// TODO: remove the above line when the AB test is resolved

import * as React from "react";
import { RadioGroup } from "@headlessui/react";
import classNames from "classnames";

import { useScrollToSelectedValue } from "../../hooks/useScrollToSelectedValue";

export type TileItemRenderProps = { isActive: boolean };

export interface TileItem {
  disabled?: boolean;
  label?: string;
  value: string;
}

export interface RenderOptionProps extends TileItem {
  compact: boolean;
  isActive: boolean;
}

export interface TilesProps<T extends TileItem> {
  /** option data to be shown in the tile group */
  items: T[];

  /** a unique name for the group */
  label?: React.ReactNode;

  /** function to call when the selected value changes internally */
  onChange: (value: string) => unknown;

  /** a function that renders the content of individual options */
  renderOption?: React.VFC<T & RenderOptionProps>;

  /** the selected value */
  value?: TileItem["value"];

  /** whether to grid tile items */
  useGrid?: boolean;

  /** whether the grid is to be sized for graphic elements */
  useGraphics?: boolean;

  /** number of grid columns */
  gridColumns?: 3 | 4 | 6;

  /** whether to transpose the grid on mobile (for 4 grid columns only) */
  reflowForMobile?: boolean;

  /** number of options to display on a single page */
  pageSize?: number | null;

  /** whether to render compact options */
  compact?: boolean;

  /** additional class to pass to the tiles */
  className?: string;

  /** whether to stretch and wrap tile items */
  // eslint-disable-next-line react/no-unused-prop-types
  stretchAndWrapItems?: boolean;
}

function defaultRenderOption({
  disabled,
  isActive,
  value,
  label,
  compact,
}: RenderOptionProps) {
  const tileClass = classNames(
    "inline-block w-full whitespace-nowrap content-center transition rounded-xs",
    "text-base leading-7 cursor-pointer text-center border py-2",
    {
      "px-1": !compact,
      "pointer-events-none": disabled || isActive,
      "opacity-50": disabled,
      "hocus:border-charcoal/50": !disabled,
      "border-lightgray": !isActive,
      "border-charcoal": isActive,
    }
  );

  return <span className={tileClass}>{label || value}</span>;
}

/**
 * A group of items from which a single, persistent selection may be made.
 */
export function Tiles<T extends TileItem>({
  items,
  onChange,
  renderOption = defaultRenderOption,
  value,
  useGrid = true,
  useGraphics = false,
  gridColumns,
  className,
  stretchAndWrapItems,
}: TilesProps<T>) {
  const scrollListRef = useScrollToSelectedValue({
    values: items.map((item) => item.value),
    selectedValue: value,
    disabled: !useGraphics,
  });

  const hasEnoughItems = items.length >= 3 || useGraphics || !useGrid;
  const containerClass = classNames(
    {
      "gap-2": !useGraphics,
      "gap-4": useGraphics,
      grid: useGrid && !useGraphics && hasEnoughItems,
      flex: !useGrid || useGraphics || !hasEnoughItems,
      "lg:grid": hasEnoughItems && (useGraphics || useGrid),
      "grid-cols-2": stretchAndWrapItems,
      "grid-cols-3":
        !stretchAndWrapItems && (gridColumns === 3 || gridColumns === 6),
      "grid-cols-2 md:grid-cols-4": gridColumns === 4,
      "px-6 lg:px-0 overflow-x-auto overflow-y-hidden no-scrollbar w-screen lg:overflow-hidden lg:w-auto":
        useGraphics,
    },
    className
  );

  return (
    <RadioGroup className="w-full" value={value} onChange={onChange}>
      <ul ref={scrollListRef} className={containerClass}>
        {items.map((item) => (
          <RadioGroup.Option
            as="li"
            className={classNames({
              "flex-1": !hasEnoughItems || stretchAndWrapItems,
              "min-w-[40%] w-2/5 lg:w-auto lg:min-w-fit": useGraphics,
            })}
            disabled={item.disabled ?? false}
            value={item.value}
            key={item.value}
          >
            {({ checked: isActive }) =>
              renderOption({
                ...item,
                isActive,
                compact: Boolean(stretchAndWrapItems),
              })
            }
          </RadioGroup.Option>
        ))}
      </ul>
    </RadioGroup>
  );
}
